// @ts-check
// Dispatches the PDP trigger event without owning customizer state, and keeps the pill's price
// badge on the metal the shopper picked.

import { Component } from '@theme/component';
import { CustomizerOpenEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';

const BADGE = '[ref="ctaPillPrice"]';

/**
 * @extends Component<{}>
 */
class CustomizerTrigger extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#adoptServedBadge);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#adoptServedBadge);
  }

  /** Request the overlay open. Bound to the button via `on:click="/open"`. */
  open() {
    this.dispatchEvent(new CustomizerOpenEvent(this.dataset.configId ?? ''));
  }

  /**
   * Swaps in the badge the server rendered for the variant now selected.
   *
   * A variant change morphs the variant picker and nothing else, so wherever the optimistic paint
   * in custom.price-preview.js stands down, this is the only thing that moves the badge.
   *
   * @param {Event} event - The variant update, once the fetched section has landed.
   */
  #adoptServedBadge = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    // Every element carrying `data-config-id` stamps `product.id` into it.
    const productId = this.dataset.configId;
    if (!productId || event.detail.data.productId !== productId) return;

    // Queried rather than taken from `this.refs`, which still points at the old span until the
    // component's mutation observer catches up, and two updates can land before it does.
    const badge = this.querySelector(BADGE);
    // Scoped to this element's own tag, so the sticky bar's copy of the pill is left to the sticky
    // bar, and a featured-product block quoting another product cannot answer for this one.
    const served = event.detail.data.html.querySelector(
      `custom-customizer-trigger[data-config-id="${productId}"] ${BADGE}`
    );
    // Cloned because later listeners on this event read the same document.
    if (badge && served) badge.replaceWith(served.cloneNode(true));
  };
}

if (!customElements.get('custom-customizer-trigger')) {
  customElements.define('custom-customizer-trigger', CustomizerTrigger);
}
